import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { loyaltyAPI } from '../lib/api';
import { cn } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Check, Scissors, Lock, Copy, Share2, Gift, Users } from 'lucide-react';
import { toast } from 'sonner';

const _fmtBirthday = (b) => {
  if (!b) return '';
  const [y, m, d] = String(b).split('T')[0].split('-');
  if (!y || !m || !d) return b;
  return `${d}/${m}`;
};

export default function MiTarjetaPage() {
  const { token } = useParams();
  const [last4, setLast4] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [flipped, setFlipped] = useState(false);

  const submitAuth = async (e) => {
    e?.preventDefault?.();
    if (last4.length !== 4 || !/^\d{4}$/.test(last4)) {
      toast.error('Ingresa 4 dígitos');
      return;
    }
    setLoading(true);
    try {
      const { data: d } = await loyaltyAPI.publicCardAccess(token, last4);
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo acceder');
    } finally {
      setLoading(false);
    }
  };

  // Auth gate
  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-200 bg-white">
          <CardContent className="p-8 text-center space-y-6">
            <div className="w-16 h-16 mx-auto rounded-full bg-slate-900 flex items-center justify-center">
              <Lock className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Tu tarjeta de lealtad</h1>
              <p className="text-sm text-slate-500 mt-1">
                Ingresa los últimos <strong>4 dígitos</strong> de tu teléfono para acceder.
              </p>
            </div>
            <form onSubmit={submitAuth} className="space-y-4">
              <Input
                type="tel"
                inputMode="numeric"
                maxLength={4}
                value={last4}
                onChange={(e) => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="••••"
                className="text-center text-3xl font-mono tracking-[0.5em] h-16 border-slate-300"
                data-testid="last4-input"
                autoFocus
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11"
                data-testid="last4-submit"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Ver mi tarjeta
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const client = data.client || {};
  const card = data.active_card;
  const stamps = card?.stamps || 0;
  const stampsSources = data.stamps_sources || [];
  const has50 = !!card?.reward_50_earned_at;
  const has50Redeemed = !!card?.reward_50_redeemed_at;
  const hasFree = !!card?.reward_free_earned_at;
  const hasFreeRedeemed = !!card?.reward_free_redeemed_at;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 py-8 px-4">
      <div className="max-w-md mx-auto space-y-4">
        <div className="text-center">
          <p className="text-sm text-slate-500 uppercase tracking-widest">
            {data.business?.name || 'Barbería'}
          </p>
          <h1 className="text-2xl font-bold mt-1" style={{ fontFamily: 'Manrope' }}>
            Tu tarjeta de lealtad
          </h1>
          <p className="text-xs text-slate-500 mt-1">Toca la tarjeta para girarla</p>
        </div>

        {/* Flip Card */}
        <div className="perspective-1000" style={{ perspective: '1000px' }}>
          <div
            className={cn(
              'relative transition-transform duration-700 cursor-pointer',
              'aspect-[85/54]'
            )}
            style={{
              transformStyle: 'preserve-3d',
              WebkitTransformStyle: 'preserve-3d',
              transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              WebkitTransform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}
            onClick={() => setFlipped((v) => !v)}
            data-testid="flip-card"
          >
            {/* FRONT */}
            <div
              className="absolute inset-0 rounded-2xl bg-black text-white shadow-xl overflow-hidden"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(0deg)',
                WebkitTransform: 'rotateY(0deg)',
              }}
            >
              <FrontDecor />
              <div className="relative z-10 h-full p-4 sm:p-6 flex flex-col justify-between">
                <div>
                  <p className="text-[9px] sm:text-[10px] tracking-[0.35em] sm:tracking-[0.4em] text-white/60 uppercase">Barrabás Barbería</p>
                  <p className="text-base sm:text-lg font-bold mt-1" style={{ fontFamily: 'Manrope' }}>Tarjeta de Lealtad</p>
                </div>
                <div>
                  <p className="text-[8px] sm:text-[9px] tracking-[0.25em] sm:tracking-[0.3em] text-white/50 uppercase">Cliente</p>
                  <p className="text-base sm:text-xl font-semibold truncate" data-testid="client-name">
                    {client.first_name} {client.last_name}
                  </p>
                  <div className="flex justify-between items-end mt-2 sm:mt-3 text-[11px] sm:text-xs gap-2">
                    <div className="min-w-0">
                      <p className="text-[8px] sm:text-[9px] tracking-[0.25em] sm:tracking-[0.3em] text-white/50 uppercase">Cumpleaños</p>
                      <p className="font-mono truncate">{_fmtBirthday(client.birthday) || '—'}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[8px] sm:text-[9px] tracking-[0.25em] sm:tracking-[0.3em] text-white/50 uppercase">ID</p>
                      <p className="font-mono text-amber-200" data-testid="client-id">
                        #{String(client.client_number || 0).padStart(4, '0')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* BACK */}
            <div
              className="absolute inset-0 rounded-2xl bg-black text-white shadow-xl overflow-hidden"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                WebkitTransform: 'rotateY(180deg)',
              }}
            >
              <BackDecor />
              <div className="relative z-10 h-full p-3 sm:p-4 flex flex-col gap-2 sm:gap-3">
                {/* stamps grid 5x2 - full width (10 total) */}
                <div className="flex-1 grid grid-cols-5 grid-rows-2 gap-1 sm:gap-1.5 min-h-0">
                  {Array.from({ length: 10 }).map((_, i) => {
                    const filled = i < stamps;
                    const source = stampsSources[i] || 'purchase';
                    const isReferral = filled && source === 'referral';
                    return (
                      <div
                        key={i}
                        className={cn(
                          'aspect-square rounded-full flex items-center justify-center border-2 mx-auto h-full max-h-full',
                          !filled && 'border-white/25 bg-white/5',
                          filled && isReferral && 'bg-sky-100 border-sky-200',
                          filled && !isReferral && 'bg-amber-100 border-amber-200',
                        )}
                        title={isReferral ? 'Sello ganado por referido' : (source === 'manual' ? 'Sello manual' : 'Sello por servicio')}
                        data-testid={`stamp-${i + 1}`}
                        data-source={filled ? source : undefined}
                      >
                        {filled ? (
                          isReferral
                            ? <Users className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-sky-800" />
                            : <Scissors className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-black" />
                        ) : (
                          <span className="text-[8px] sm:text-[10px] text-white/30">{i + 1}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* rewards row - horizontal below stamps */}
                <div className="grid grid-cols-2 gap-2 shrink-0">
                  <RewardPill label="50% descuento" earned={has50} redeemed={has50Redeemed} />
                  <RewardPill label="Servicio GRATIS" earned={hasFree} redeemed={hasFreeRedeemed} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center text-xs text-slate-500">
          {stamps} / 10 sellos • {10 - stamps} para completar
        </div>

        <ReferralShareCard token={token} businessName={data.business?.name} />

        {(data.completed_cards?.length || 0) > 0 && (
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                Historial de tarjetas completadas
              </p>
              <div className="space-y-2">
                {data.completed_cards.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1">
                    <span className="text-slate-700">Tarjeta #{c.card_number}</span>
                    <span className="text-xs text-slate-500">
                      {c.completed_at ? new Date(c.completed_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function ReferralShareCard({ token, businessName }) {
  const [copied, setCopied] = useState(false);
  const referralUrl = `${window.location.origin}/referido/${token}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast.success('Link copiado');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const shareLink = async () => {
    const shareData = {
      title: businessName ? `${businessName} — Programa de Lealtad` : 'Programa de Lealtad',
      text: '¡Regístrate con mi link y ambos ganamos un sello en tu primera visita!',
      url: referralUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // user cancelled - ignore
      }
    } else {
      copyLink();
    }
  };

  return (
    <Card className="border-slate-200 bg-white overflow-hidden" data-testid="referral-share-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
            <Gift className="w-4 h-4 text-amber-700" />
          </div>
          <div>
            <p className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Invita a un amigo
            </p>
            <p className="text-[11px] text-slate-500 leading-tight">
              Cuando complete su primer servicio, ambos ganan 1 sello.
            </p>
          </div>
        </div>

        <div className="flex gap-4 items-center">
          <div className="p-2 rounded-lg bg-white border border-slate-200 shrink-0" data-testid="referral-qr">
            <QRCodeCanvas value={referralUrl} size={96} level="M" includeMargin={false} />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="text-[11px] font-mono text-slate-600 truncate bg-slate-50 rounded px-2 py-1 border border-slate-200"
              data-testid="referral-url">
              {referralUrl}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={copyLink}
                className="h-8"
                data-testid="copy-referral-btn"
              >
                {copied
                  ? <Check className="w-3.5 h-3.5 mr-1 text-emerald-600" />
                  : <Copy className="w-3.5 h-3.5 mr-1" />}
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={shareLink}
                className="h-8 bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="share-referral-btn"
              >
                <Share2 className="w-3.5 h-3.5 mr-1" />
                Compartir
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RewardPill({ label, earned, redeemed }) {
  return (
    <div
      className={cn(
        'rounded-full h-8 sm:h-9 px-2 flex items-center justify-center gap-1 relative text-[10px] sm:text-xs font-semibold whitespace-nowrap',
        earned && !redeemed
          ? 'bg-amber-200 text-black shadow-lg ring-2 ring-amber-300 animate-pulse'
          : redeemed
            ? 'bg-emerald-100 text-emerald-800'
            : 'border-2 border-white/25 text-white/40'
      )}
    >
      <span className="truncate">{label}</span>
      {redeemed && (
        <Check className="w-3 h-3 shrink-0 text-emerald-600" />
      )}
    </div>
  );
}

function RewardCircle({ label, earned, redeemed }) {
  return (
    <div
      className={cn(
        'rounded-full aspect-square flex items-center justify-center relative',
        earned && !redeemed
          ? 'bg-amber-200 text-black shadow-lg ring-2 ring-amber-300 animate-pulse'
          : redeemed
            ? 'bg-emerald-100 text-emerald-800'
            : 'border-2 border-white/25 text-white/40'
      )}
    >
      <span className="font-bold text-sm">{label}</span>
      {redeemed && (
        <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center">
          <Check className="w-3 h-3" />
        </div>
      )}
    </div>
  );
}

function FrontDecor() {
  return (
    <>
      <svg className="absolute top-2 left-2 opacity-10 w-16 h-16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14.7 6.3l1-1 1 1L15.7 7.3zM10.3 6.3l-1-1-1 1L9.3 7.3zM12 2C10.3 4 8 6.5 8 9c0 2 2 3 4 3s4-1 4-3c0-2.5-2.3-5-4-7z"/>
      </svg>
      <svg className="absolute bottom-2 right-2 opacity-10 w-24 h-24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
        <circle cx="6" cy="18" r="3"/>
        <circle cx="18" cy="6" r="3"/>
        <path d="M8 16L20 4M8 20L20 8"/>
      </svg>
      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent" />
    </>
  );
}

function BackDecor() {
  return (
    <div className="absolute inset-0 bg-gradient-to-tr from-black via-black to-slate-900" />
  );
}
